import { Button } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ReactComponent as CheckmarkIcon } from 'images/checkmarkIcon.svg';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { getStartingIn, isDateBefore } from 'services/helper-services/utils';
import { useInjection } from 'services/ioc/ioc.react';
import { badgeBackgroundColor } from 'styles/Colors';
import { IApiScheduleResponse } from '../../../interfaces/team/IApiScheduleResponse';
import {
  BackgroundOverlay,
  Badge,
  BadgeWrapper,
  GameDetails,
  GameId,
  ScheduledFor,
  ScheduledGameTileWrapper,
  TaggedWrapper,
  TeamName,
  TileGradientOverlay,
  VersusOverlay,
} from './ScheduledGameTileStyles';

const MLB_EXTERNAL_SESSION_PREFIX = 'MLB-';

export interface IProps {
  backgroundImage: string;
  isNextGame?: boolean;
  game: IApiScheduleResponse;
  onClickCallback: () => void;
  buttonVariant?: 'primary' | 'secondary';
  index: number;
}
export const ScheduledGameTile: React.FC<IProps> = observer((props: IProps) => {
  const { backgroundImage, isNextGame, game, onClickCallback, buttonVariant, index } = props;
  const [startingIn, setStartingIn] = useState<string>(getStartingIn(game.armingTime));
  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);
  let interval: ReturnType<typeof setInterval>;

  useEffect(() => {
    interval = setInterval(() => {
      setStartingIn(getStartingIn(game.armingTime));
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [game]);

  const isStartAvailable = () => {
    if (featureConfigService.getConfig().isOperatorlessTaggingEnabled && game.externalSessionId?.includes(MLB_EXTERNAL_SESSION_PREFIX)) {
      return true;
    }

    return game.numberOfSessions === 0;
  };

  return (
    <ScheduledGameTileWrapper
      isNextGame={isNextGame}
      style={{ backgroundImage: `url(${backgroundImage})` }}
      data-testid={`scheduled-game-tile-${index}`}
    >
      <BackgroundOverlay />
      <VersusOverlay>VS</VersusOverlay>
      <TileGradientOverlay>
        <TeamName isTagged={game.numberOfSessions > 0} data-testid={`away-team-${index}`}>
          {game.awayTeam.teamName}
        </TeamName>
        <TeamName isTagged={game.numberOfSessions > 0} data-testid={`home-team-${index}`}>
          {game.homeTeam.teamName}
        </TeamName>

        <BadgeWrapper>
          {startingIn.length > 0 && (
            <Badge backgroundColor={badgeBackgroundColor} opacity={isDateBefore(game.armingTime) ? 0.6 : 1}>
              {startingIn}
            </Badge>
          )}
          {featureConfigService.getConfig().isOperatorlessTaggingEnabled && <Badge backgroundColor={'#5890DE'}>Auto-tag enabled</Badge>}
        </BadgeWrapper>
        <GameDetails>
          <div>
            <ScheduledFor isTagged={game.numberOfSessions > 0} data-testid={`scheduled-for-${index}`}>
              Scheduled for {game.localExpectedTimeDisplay}
            </ScheduledFor>
            <GameId data-testid={`game-id-${index}`}>Game ID: {game.externalSessionId}</GameId>
          </div>
          {!isStartAvailable() ? (
            <TaggedWrapper data-testid={`tagged-wrapper-${index}`}>
              Tagged <CheckmarkIcon />
            </TaggedWrapper>
          ) : (
            <Button
              label='Tag game'
              intent={buttonVariant || 'secondary'}
              onClick={onClickCallback}
              width={88}
              data-testid={`tag-game-btn-${index}`}
            />
          )}
        </GameDetails>
      </TileGradientOverlay>
    </ScheduledGameTileWrapper>
  );
});
